import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { activitiesApi, type ApiListResponse } from '@/lib/api'
import { useNotificationStore } from './notifications'
import type { Activity } from '@/types'

export type { Activity }

export interface ActivityStats {
  total: number
  open: number
  done: number
  overdue: number
}

export interface ActivitiesState {
  activities: Activity[]
  loading: boolean
  error: string | null
  meta: {
    page: number
    pageSize: number
    total: number
    hasNext: boolean
  } | null
  stats: ActivityStats | null
}

export const useActivitiesStore = defineStore('activities', () => {
  const activities = ref<Activity[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const meta = ref<ActivitiesState['meta']>(null)
  const stats = ref<ActivityStats | null>(null)
  
  const notifications = useNotificationStore()

  // Computed
  const activitiesCount = computed(() => activities.value.length)

  const activitiesByStatus = computed(() => {
    const grouped: Record<string, Activity[]> = {}
    activities.value.forEach(activity => {
      if (!grouped[activity.status]) {
        grouped[activity.status] = []
      }
      grouped[activity.status]!.push(activity)
    })
    return grouped
  })

  const activitiesByType = computed(() => {
    const grouped: Record<string, Activity[]> = {}
    activities.value.forEach(activity => {
      if (!grouped[activity.type]) {
        grouped[activity.type] = []
      }
      grouped[activity.type]!.push(activity)
    })
    return grouped
  })

  const activitiesByOwner = computed(() => {
    const grouped: Record<string, Activity[]> = {}
    activities.value.forEach(activity => {
      if (!grouped[activity.ownerId]) {
        grouped[activity.ownerId] = []
      }
      grouped[activity.ownerId]!.push(activity)
    })
    return grouped
  })

  const openActivities = computed(() => 
    activities.value.filter(activity => activity.status === 'open')
  )

  const completedActivities = computed(() => 
    activities.value.filter(activity => activity.status === 'done')
  )

  const overdueActivities = computed(() => {
    const now = new Date()
    return activities.value.filter(activity => 
      activity.status === 'open' && new Date(activity.dueAt) < now
    )
  })

  const todaysActivities = computed(() => {
    const now = new Date()
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const todayEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)

    return activities.value.filter(activity => {
      if (!activity.dueAt) return false
      const dueDate = new Date(activity.dueAt)
      return dueDate >= todayStart && dueDate < todayEnd
    })
  })

  const upcomingActivities = computed(() => {
    const today = new Date()
    const nextWeek = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000)
    
    return activities.value.filter(activity => {
      if (!activity.dueAt) return false
      const dueDate = new Date(activity.dueAt)
      return dueDate > today && dueDate <= nextWeek && activity.status === 'open'
    })
  })

  // Actions
  async function fetchActivities(
    params?: any,
    options: { append?: boolean; fetchAll?: boolean } = {},
  ): Promise<void> {
    const { append = false, fetchAll = false } = options
    loading.value = true
    error.value = null

    try {
      const queryParams = { includeRelated: true, pageSize: 100, ...params }
      const response = await activitiesApi.list(queryParams) as ApiListResponse<Activity, ActivityStats>

      if (fetchAll) {
        // Dashboard consumers need the full dataset to compute stats client-side.
        let allData = [...response.data]
        let currentMeta = response.meta
        while (currentMeta?.hasNext) {
          const nextPage = (currentMeta.page || 1) + 1
          const nextResponse = await activitiesApi.list({ ...queryParams, page: nextPage }) as ApiListResponse<Activity, ActivityStats>
          allData.push(...nextResponse.data)
          currentMeta = nextResponse.meta
        }
        activities.value = allData
        meta.value = currentMeta ? { ...currentMeta, total: allData.length } : null
      } else if (append) {
        activities.value = [...activities.value, ...response.data]
        meta.value = response.meta
      } else {
        activities.value = response.data
        meta.value = response.meta
      }

      // Stats are only returned when includeStats=true. Keep the prior value when
      // omitted so pagination clicks don't blank out the status pills.
      if (response.stats) {
        stats.value = response.stats
      }
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch activities'
      notifications.error('Failed to load activities')
    } finally {
      loading.value = false
    }
  }

  async function fetchActivity(id: string): Promise<Activity | null> {
    loading.value = true
    error.value = null

    try {
      const response = await activitiesApi.get(id, { includeRelated: true })
      const activity = response.data
      
      // Update activity in store if it exists
      const index = activities.value.findIndex(a => a._id === id)
      if (index !== -1) {
        activities.value[index] = activity
      } else {
        activities.value.push(activity)
      }
      
      return activity
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch activity'
      notifications.error('Failed to load activity')
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * `rethrow` is for form dialogs, which give their own feedback: no success or error toast, and
   * a failure is thrown instead of returning null, so the dialog can show the API's message (and a
   * demo-org rejection shows only its own info toast). The store state and the action itself (which
   * `$onAction` listeners such as DealNextStepCard watch) are the same either way.
   */
  async function createActivity(
    activityData: Partial<Activity>,
    opts: { rethrow?: boolean } = {},
  ): Promise<Activity | null> {
    loading.value = true
    error.value = null

    try {
      const response = await activitiesApi.create(activityData)
      const newActivity = response.data
      activities.value.push(newActivity)
      if (!opts.rethrow) notifications.success('Activity created successfully')
      return newActivity
    } catch (err: any) {
      console.error('Activities store: Create activity error:', err)
      error.value = err.response?.data?.error?.message || 'Failed to create activity'
      if (opts.rethrow) throw err
      notifications.error('Failed to create activity')
      return null
    } finally {
      loading.value = false
    }
  }

  async function updateActivity(
    id: string,
    activityData: Partial<Activity>,
    opts: { rethrow?: boolean } = {},
  ): Promise<Activity | null> {
    loading.value = true
    error.value = null

    try {
      const response = await activitiesApi.update(id, activityData)
      const updatedActivity = response.data

      const index = activities.value.findIndex(a => a._id === id)
      if (index !== -1) {
        activities.value[index] = updatedActivity
      }

      if (!opts.rethrow) notifications.success('Activity updated successfully')
      return updatedActivity
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to update activity'
      if (opts.rethrow) throw err
      notifications.error('Failed to update activity')
      return null
    } finally {
      loading.value = false
    }
  }

  async function deleteActivity(id: string): Promise<boolean> {
    loading.value = true
    error.value = null
    
    try {
      await activitiesApi.delete(id)
      activities.value = activities.value.filter(a => a._id !== id)
      notifications.success('Activity deleted successfully')
      return true
    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to delete activity'
      notifications.error('Failed to delete activity')
      return false
    } finally {
      loading.value = false
    }
  }

  async function completeActivity(id: string): Promise<Activity | null> {
    const activity = activities.value.find(a => a._id === id)
    if (!activity) return null

    return await updateActivity(id, {
      status: 'done'
    })
  }

  // Note: Overdue is computed based on dueAt date, not a status

  async function fetchContactActivities(contactId: string, params?: any): Promise<void> {
    loading.value = true
    error.value = null

    try {
      const response: ApiListResponse<Activity> = await activitiesApi.getByContact(contactId, { includeRelated: true, ...params })
      const contactActivities = response.data

      // Remove existing activities for this contact and add new ones
      activities.value = activities.value.filter(activity => activity.contactId !== contactId)
      activities.value.push(...contactActivities)

      // Sort by due date (earliest first) and creation date
      activities.value.sort((a, b) => {
        if (a.dueAt && b.dueAt) {
          return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime()
        }
        if (a.dueAt && !b.dueAt) return -1
        if (!a.dueAt && b.dueAt) return 1
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })

    } catch (err: any) {
      error.value = err.response?.data?.error?.message || 'Failed to fetch contact activities'
      notifications.error('Failed to load activities')
    } finally {
      loading.value = false
    }
  }

  function getActivitiesByEntity(entityType: string, entityId: string): Activity[] {
    return activities.value.filter(activity =>
      activity.entityType === entityType && activity.entityId === entityId
    )
  }

  function getContactActivities(contactId: string): Activity[] {
    return activities.value.filter(activity => activity.contactId === contactId)
  }

  function getActivityById(id: string): Activity | undefined {
    return activities.value.find(a => a._id === id)
  }

  function getActivityPriorityColor(priority: string): string {
    switch (priority) {
      case 'urgent':
        return 'text-danger-text bg-danger-tint'
      case 'high':
        return 'text-warning-text bg-warning-tint'
      case 'normal':
        return 'text-ink-2 bg-wash'
      case 'low':
        return 'text-ink-3 bg-wash'
      default:
        return 'text-ink-2 bg-wash'
    }
  }

  function getActivityStatusColor(status: string): string {
    switch (status) {
      case 'done':
        return 'text-success-text bg-success-tint'
      case 'open':
        return 'text-info-text bg-info-tint'
      case 'cancelled':
        return 'text-ink-3 bg-wash'
      default:
        return 'text-ink-3 bg-wash'
    }
  }

  function getActivityTypeIcon(type: string): string {
    switch (type) {
      case 'call':
        return 'phone'
      case 'meeting':
        return 'calendar'
      case 'task':
        return 'clipboard'
      default:
        return 'document'
    }
  }

  function clearActivities(): void {
    activities.value = []
    meta.value = null
    stats.value = null
    error.value = null
  }

  return {
    // State
    activities,
    loading,
    error,
    meta,
    stats,
    
    // Computed
    activitiesCount,
    activitiesByStatus,
    activitiesByType,
    activitiesByOwner,
    openActivities,
    completedActivities,
    overdueActivities,
    todaysActivities,
    upcomingActivities,
    
    // Actions
    fetchActivities,
    fetchActivity,
    fetchContactActivities,
    createActivity,
    updateActivity,
    deleteActivity,
    completeActivity,
    getActivitiesByEntity,
    getContactActivities,
    getActivityById,
    getActivityPriorityColor,
    getActivityStatusColor,
    getActivityTypeIcon,
    clearActivities,
  }
})